import { addMonths, monthsBetween, type Day } from '../core/calendar.ts';
import { add, dec, div, fromInt, gt, lte, max, mul, pow, sub, ZERO, type Decimal } from '../core/decimal.ts';
import { monthlyRate } from '../calculations/loan-math.ts';
import type { Mortgage } from '../types/domain.ts';

/**
 * Plan.calculateMortgageBalance (divergence D8a): the annuity payment from the original loan
 * (pow via Double) iterated once per whole month elapsed; linear at 0%; rate changes ignored.
 */
export function calculateMortgageBalance(mortgage: Mortgage, date: Day): Decimal {
  const elapsed = monthsBetween(mortgage.startDate, date);
  if (elapsed <= 0) return mortgage.loanAmount;
  const totalMonths = mortgage.termYears * 12;
  if (elapsed >= totalMonths && !mortgage.isInterestOnly) return ZERO;
  if (mortgage.isInterestOnly) return mortgage.loanAmount;

  const rate = monthlyRate(mortgage.interestRate);
  if (!gt(rate, ZERO)) {
    const paidOff = div(mul(mortgage.loanAmount, fromInt(elapsed)), fromInt(totalMonths));
    return max(sub(mortgage.loanAmount, paidOff), ZERO);
  }

  const growth = pow(add(dec(1), rate), fromInt(totalMonths));
  const payment = div(mul(mul(mortgage.loanAmount, rate), growth), sub(growth, dec(1)));
  let balance = mortgage.loanAmount;
  for (let month = 0; month < elapsed && !lte(balance, ZERO); month++) {
    balance = sub(balance, sub(payment, mul(balance, rate)));
  }
  return max(balance, ZERO);
}

/**
 * MetricsCalculator's level payment over the remaining term: P·r(1+r)^n / ((1+r)^n − 1), the
 * power via Double; P/n at 0% or when the denominator is not positive; 0 without principal or term.
 */
function remainingTermPayment(principal: Decimal, annualRatePercent: Decimal, remainingMonths: number): Decimal {
  if (remainingMonths <= 0 || !gt(principal, ZERO)) return ZERO;
  const rate = monthlyRate(annualRatePercent);
  const months = fromInt(remainingMonths);
  if (lte(rate, ZERO)) return div(principal, months);
  const growth = pow(add(dec(1), rate), months);
  const denominator = sub(growth, dec(1));
  if (!gt(denominator, ZERO)) return div(principal, months);
  return div(mul(mul(principal, rate), growth), denominator);
}

/**
 * MetricsCalculator's mortgage balance (divergence D8b): each month re-amortises the balance
 * over the remaining term at the rate in force (rate changes applied once the month's anchored
 * date reaches them), then pays interest and principal. The loan before the first whole month
 * or for interest-only; 0 after the term or once the balance reaches 0.
 */
export function reamortisedMortgageBalance(mortgage: Mortgage, date: Day): Decimal {
  const elapsed = monthsBetween(mortgage.startDate, date);
  if (elapsed <= 0) return mortgage.loanAmount;
  const totalMonths = mortgage.termYears * 12;
  if (elapsed >= totalMonths && !mortgage.isInterestOnly) return ZERO;
  if (mortgage.isInterestOnly) return mortgage.loanAmount;

  const changes = [...mortgage.rateChanges].sort((a, b) => a.date - b.date);
  let changeIndex = 0;
  let rate = mortgage.interestRate;
  let balance = mortgage.loanAmount;
  for (let month = 0; month < elapsed; month++) {
    const monthDate = addMonths(mortgage.startDate, month);
    while (changeIndex < changes.length && monthDate >= changes[changeIndex]!.date) {
      rate = changes[changeIndex]!.newRate;
      changeIndex += 1;
    }
    const remainingMonths = totalMonths - month;
    if (remainingMonths <= 0) break;
    const payment = remainingTermPayment(balance, rate, remainingMonths);
    const interest = mul(balance, monthlyRate(rate));
    balance = sub(balance, sub(payment, interest));
    if (lte(balance, ZERO)) return ZERO;
  }
  return max(ZERO, balance);
}
